import { createContext, FC, PropsWithChildren } from 'react';
import CreateBenefitSetStore from '../store';
import {
  usePostBenefitSet,
  usePatchBenefitSet,
  usePostBenefitSetEmployees
} from '../../../../queries/benefit/benefitSet/mutations';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../hooks/useStore';
import { cardOfferAction, useSaveCardOffer } from '../../../../queries/pxtraCard';
import { useCardOfferApplies } from './useCardOfferApplies';
import { getApiErrorMessage } from '../../../../lib/util/apiErrorMessage';
import { cardOfferSaveError } from '../store/cardOffer';

const store = new CreateBenefitSetStore();
const CreateBenefitSetContext = createContext(store);

export const CreateBenefitSetContextProvider: FC<PropsWithChildren> = ({ children }) => {
  return (
    <CreateBenefitSetContext.Provider value={store}>{children}</CreateBenefitSetContext.Provider>
  );
};

export const useCreateBenefitSet = () => {
  const postMutation = usePostBenefitSet();
  const patchMutation = usePatchBenefitSet();
  const postEmployeesMutation = usePostBenefitSetEmployees();
  const { companyStore } = useStore();
  const saveCardOfferMutation = useSaveCardOffer(companyStore.company?.id ?? '');
  const cardOfferApplies = useCardOfferApplies();
  const { t } = useTranslation();

  const createBenefitSet = async (companyId: string) => {
    try {
      const requestBody = store.buildRequest();
      const result = await postMutation.mutateAsync({ companyId, requestBody });
      store.editingBenefitSetId = result.id;
      return result;
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t('benefitSet:couldNotBeCreated'), { variant: 'error' });
    }
  };

  const updateBenefitSet = async (
    benefitSetId: string,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    try {
      const requestBody = store.buildRequest();
      const result = await patchMutation.mutateAsync({ benefitSetId, requestBody });
      if (result.confirmation_required) {
        onError?.();
      } else {
        onSuccess?.();
      }
      return result;
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(error, t, 'benefitSet') ?? t('benefitSet:couldNotBeUpdated'),
        { variant: 'error' }
      );
    }
  };

  /** Brings the set's card offer in line with the panel. False when that failed;
   * the caller shows the error, the set itself is saved by then. */
  const saveCardOffer = async (benefitSetId: string): Promise<boolean> => {
    const action = cardOfferAction(
      store.cardOfferBaseline,
      store.desiredCardOffer(cardOfferApplies)
    );
    if (action.kind === 'none') return true;

    store.cardOfferSavePending = true;
    store.cardOfferSaveError = undefined;
    try {
      store.markCardOfferSaved(await saveCardOfferMutation.mutateAsync({ benefitSetId, action }));
      return true;
    } catch (error) {
      console.error(error);
      const saveError = cardOfferSaveError(error);
      store.cardOfferSaveError = saveError;
      // The set is saved by now and editing it again goes through the edit
      // flow, so a rejected offer must not keep the way back closed.
      if (saveError.permanent) store.cardOfferSavePending = false;
      return false;
    }
  };

  const assignBenefitSet = async (
    setId: string,
    accountingPeriodMonth: number,
    accountingPeriodYear: number
  ) => {
    if (!setId) return;
    try {
      const requestBody = store.buildAssignmentRequest();
      return await postEmployeesMutation.mutateAsync({
        setId,
        accountingPeriodMonth,
        accountingPeriodYear,
        requestBody
      });
    } catch (error) {
      console.error('Error creating benefit set:', error);
      throw error;
    }
  };

  return {
    newBenefitSet: store,
    createBenefitSet,
    assignBenefitSet,
    updateBenefitSet,
    saveCardOffer,
    isUpdating: patchMutation.isPending,
    isSavingCardOffer: saveCardOfferMutation.isPending
  };
};
